// No-Show Prevention Types

export enum RiskLevel {
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
  Critical = 'Critical'
}

export interface RiskFactor {
  factor: string;
  weight: number;
  description: string;
}

export interface BookingRiskAssessment {
  bookingId: number;
  userId: string;
  riskScore: number;
  riskLevel: RiskLevel;
  riskFactors: RiskFactor[];
  requiresConfirmation: boolean;
  requiresDeposit: boolean;
  depositAmount: number;
  assessedAt: string;
}

export interface ConfirmationRequest {
  bookingId: number;
  confirmationMethod: 'Email' | 'SMS' | 'Manual';
  confirmedAt?: string;
  confirmedBy?: string;
}

export interface ConfirmationStatus {
  bookingId: number;
  isConfirmed: boolean;
  confirmationRequired: boolean;
  confirmationDeadline?: string;
  hoursUntilAutoCancellation?: number;
  confirmationMethod?: string;
  confirmedAt?: string;
}

export interface DepositPaymentRequest {
  bookingId: number;
  amount: number;
  currency: string;
  description: string;
}

export interface DepositPaymentResponse {
  success?: boolean;
  paymentIntentId?: string;
  /** Null when a gift card fully covered the deposit (no card payment needed) */
  clientSecret: string | null;
  amount: number;
  currency?: string;
  status?: string;
  /** Portion of the deposit covered by an applied gift card (REV-2) */
  giftCardAmountApplied?: number;
  /** Remainder still owed by card after any gift card application */
  amountDue?: number;
  /** True when the deposit was settled entirely by the gift card */
  depositPaid?: boolean;
  /** Populated on failure responses (e.g. unknown/inactive gift card code) */
  errorMessage?: string;
  /**
   * True only when the returned intent was created with SetupFutureUsage
   * (saveCardForFutureUse honored, GAP-4). Always false on the full-cover
   * gift-card path (depositPaid true, no intent) and on errors.
   */
  cardWillBeSaved?: boolean;
}

export interface DepositStatus {
  bookingId: number;
  depositRequired: boolean;
  depositAmount: number;
  depositPaid: boolean;
  depositPaidAt?: string;
  depositTransactionId?: string;
  depositRefundable: boolean;
}

export interface BookingWithRiskInfo {
  bookingId: number;
  userId: string;
  userName: string;
  userEmail: string;
  barbershopId: number;
  barbershopName: string;
  barberId: number;
  barberName: string;
  bookingDateTime: string;
  totalPrice: number;
  status: string;
  riskAssessment?: BookingRiskAssessment;
  confirmationStatus: ConfirmationStatus;
  depositStatus: DepositStatus;
  hoursUntilBooking: number;
}

// No-Show Confirmation Gate (GAP-1)

/**
 * A booking auto-marked NoShow by the background heuristic that is awaiting
 * barber/admin review before any fee is charged or deposit forfeited.
 */
export interface PendingNoShowConfirmation {
  bookingId: number;
  barberId: number;
  barberName: string;
  customerName: string;
  serviceName: string;
  /** Scheduled appointment time (UTC ISO string) */
  bookingDateTime: string;
  /** When the background heuristic auto-marked the booking NoShow (UTC ISO string) */
  noShowAutoMarkedAt?: string | null;
  /** Deposit amount on the booking (forfeited on confirm when paid) */
  depositAmount: number;
  depositPaid: boolean;
  /** No-show fee that would be charged on confirm (0 = policy disabled/exempt) */
  potentialFeeAmount: number;
}

/** Result of confirming or dismissing a pending auto-detected no-show */
export interface NoShowConfirmationResult {
  bookingId: number;
  /** "Confirmed" or "Dismissed" */
  action: string;
  message: string;
}

// Analytics Types
export interface NoShowPreventionMetrics {
  totalBookingsWithRiskAssessment: number;
  lowRiskCount: number;
  mediumRiskCount: number;
  highRiskCount: number;
  criticalRiskCount: number;
  confirmationsRequired: number;
  confirmationsReceived: number;
  confirmationRate: number;
  depositsRequired: number;
  depositsCollected: number;
  depositCollectionRate: number;
  autoCancellations: number;
  noShowsPrevented: number;
  estimatedRevenueSaved: number;
}

export interface RiskScoreDistribution {
  riskLevel: RiskLevel;
  count: number;
  percentage: number;
  averageScore: number;
}

export interface ConfirmationRateByRisk {
  riskLevel: RiskLevel;
  confirmationsRequired: number;
  confirmationsReceived: number;
  confirmationRate: number;
  autoCancellations: number;
}

export interface DepositImpactMetrics {
  depositsRequired: number;
  depositsCollected: number;
  totalDepositAmount: number;
  depositsForfeited: number;
  forfeitedAmount: number;
  averageDepositAmount: number;
  collectionRate: number;
}

export interface PreventionEffectiveness {
  date: string;
  totalBookings: number;
  riskAssessmentsRun: number;
  confirmationsRequested: number;
  confirmationsReceived: number;
  depositsRequired: number;
  depositsCollected: number;
  autoCancellations: number;
  actualNoShows: number;
  preventionRate: number;
}

export interface NoShowPreventionAnalytics {
  metrics: NoShowPreventionMetrics;
  riskDistribution: RiskScoreDistribution[];
  confirmationRates: ConfirmationRateByRisk[];
  depositImpact: DepositImpactMetrics;
  effectiveness: PreventionEffectiveness[];
  topRiskFactors: RiskFactor[];
}

// Filter and Query Types
export interface NoShowPreventionFilters {
  startDate?: string;
  endDate?: string;
  riskLevel?: RiskLevel;
  confirmationStatus?: 'All' | 'Required' | 'Confirmed' | 'Pending';
  depositStatus?: 'All' | 'Required' | 'Paid' | 'Pending';
  barbershopId?: number;
}

export interface BookingConfirmationBulkRequest {
  bookingIds: number[];
  confirmationMethod: 'Email' | 'SMS' | 'Manual';
}
